// =====================================================================
// V4C /search ページ用 results list Web Component
// =====================================================================
//
// 役割:
//   - <v4c-results-list> custom element として /search ページの結果 grid を担当
//   - connectedCallback で V4cClient.searchFull → 完成 section HTML hydrate → grid append
//   - IntersectionObserver で無限スクロール
//   - 'v4c:filter-update' イベントを <v4c-facets-form> から受けて grid 再描画
//
// 案 M-1 で前提:
//   - filter UI は <v4c-facets-form> が一手に担当 (URL 同期 + V4C 呼出 + facet
//     DOM 構築 + 'v4c:filter-update' dispatch)
//   - 本 component は grid 描画のみに集中
//   - Horizon native の sectionRenderer.renderSection / morph 経路は使わない
//   - 旧 polling / snapshot restore / refresh from Shopify / kickInflightFetch /
//     applier 連携は廃止 (= 別 source of truth が不要になった)
// =====================================================================

(function () {
  function collectFilterParams() {
    const src = new URLSearchParams(window.location.search);
    const out = new URLSearchParams();
    src.forEach((value, key) => {
      if (key.indexOf('filter.') === 0 || key === 'sort_by') out.append(key, value);
    });
    return out;
  }

  if (customElements.get('v4c-results-list')) return;

  class V4cResultsListComponent extends HTMLElement {
    constructor() {
      super();
      this.query = '';
      this.pageSize = 24;
      this.page = 1;
      this.allLoaded = false;
      this.loading = false;
      this.totalSize = null;
      this.loadedCount = 0;
      this.filtersKey = '';
    }

    connectedCallback() {
      this.query = (this.getAttribute('data-query') || '').trim();
      if (!this.query) {
        const noQ = this.querySelector('[data-v4c-no-query]');
        if (noQ) noQ.hidden = false;
        return;
      }
      this.pageSize = parseInt(this.getAttribute('data-page-size') || '24', 10) || 24;
      this.grid = this.querySelector('.product-grid');
      this.sentinel = this.querySelector('[data-v4c-sentinel]');
      this.emptyState = this.querySelector('.v4c-empty-state');
      this.filtersKey = collectFilterParams().toString();

      if (!window.V4cClient) {
        console.warn('[v4c-results-list] V4cClient not available');
        this.classList.add('v4c-error');
        return;
      }

      // IntersectionObserver は loadNextPage より先に立てる:
      // 初回 fetch 中の callback fire は loading フラグで skip され、完了後に
      // まだ sentinel が viewport 内ならそのまま page 2 がロードされる。
      this.setupInfiniteScroll();
      this.loadNextPage();

      // <v4c-facets-form> からの 'v4c:filter-update' イベントを受けて grid 再描画。
      // event.detail.v4cResult があれば二重 fetch 回避のためそれをそのまま使う。
      this._onFilterUpdate = (e) => this._handleFilterUpdate(e);
      document.addEventListener('v4c:filter-update', this._onFilterUpdate);
    }

    disconnectedCallback() {
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
      if (this._onFilterUpdate) {
        document.removeEventListener('v4c:filter-update', this._onFilterUpdate);
        this._onFilterUpdate = null;
      }
    }

    _handleFilterUpdate(event) {
      const newFiltersKey = (event?.detail?.filtersKey) ?? collectFilterParams().toString();
      // filter / sort 変更がなければ skip (e.g. _fetchAndRender の初回 broadcast を
      // 既に connectedCallback で消化済の場合)
      if (newFiltersKey === this.filtersKey && this.totalSize !== null) return;
      this.filtersKey = newFiltersKey;
      this._resetAndReload(event?.detail?.v4cResult);
    }

    _resetAndReload(preFetchedV4cResult) {
      this.page = 1;
      this.allLoaded = false;
      this.loading = false;
      this.totalSize = null;
      this.loadedCount = 0;

      // grid を skeleton に戻す
      if (this.grid) {
        while (this.grid.firstChild) this.grid.removeChild(this.grid.firstChild);
        const skeletonCount = parseInt(this.getAttribute('data-skeleton-count') || '8', 10) || 8;
        for (let i = 0; i < skeletonCount; i++) {
          const li = document.createElement('li');
          li.className = 'product-grid__item product-grid__item--skeleton';
          li.setAttribute('aria-hidden', 'true');
          const skel = document.createElement('div');
          skel.className = 'v4c-skeleton-card';
          li.appendChild(skel);
          this.grid.appendChild(li);
        }
      }
      if (this.emptyState) this.emptyState.hidden = true;
      this.classList.remove('v4c-loaded', 'v4c-error');

      this.loadNextPage(preFetchedV4cResult);
    }

    async loadNextPage(preFetchedV4cResult) {
      if (this.allLoaded || this.loading) return;
      this.loading = true;
      try {
        let v4c;
        if (preFetchedV4cResult && this.page === 1) {
          // <v4c-facets-form> が既に V4C を叩いている → そのレスポンスを再利用
          // (二重 fetch 回避)
          v4c = preFetchedV4cResult;
        } else {
          // includeSection を渡さない: backend (wishlist_api) 経由の section render は
          // default locale / currency なので JPY 表示になってしまう (Markets / locale
          // prefix を伴わないため)。商品 HTML は必ず client-side で
          // routes.search_url 経由で叩き、storefront の locale + Markets を反映させる。
          v4c = await window.V4cClient.searchFull({
            query: this.query,
            page: this.page,
            pageSize: this.pageSize,
            filters: this.filtersKey,
          });
        }

        const productIds = v4c.results.map((r) => r.productId).filter(Boolean);

        if (this.totalSize === null) {
          this.totalSize = v4c.totalSize || 0;
          this.updateDocumentTitle();
        }

        // Custom Pixel に search イベントを通知する。
        // Shopify ネイティブの search_submitted は v4c 表示結果と payload が
        // 不一致になり V4C 側で no-result 扱いになるため、ここから明示 publish する。
        //
        // 2026-05-27: 旧実装は page === 1 のときだけ publish していたが、V4C 管理画面の
        // 「% of search events with page offset > 1」しきい値 1.5% と
        // 「% of search events with product impressions」しきい値 95% が ~0% で
        // パフォーマンス影響大判定になっていたため、 hit がある page は全て publish する
        // ように変更。ページ送り = ユーザが結果を更に見たという engagement シグナルで、
        // Vertex の relevance learning にも positive に働く。
        // ただし ゼロヒットは publish しない (productDetails 空の search event は
        // impressions 比率を直接下げるため。zero-hit 自体は別途 BQ ログで監視する)。
        if (productIds.length > 0) {
          this._publishSearchCompleted(v4c);
        }

        if (productIds.length === 0) {
          if (this.page === 1 && this.grid) {
            this.grid.querySelectorAll('.product-grid__item--skeleton').forEach((el) => el.remove());
          }
          this.allLoaded = true;
          if (this.page === 1 && this.emptyState) this.emptyState.hidden = false;
          return;
        }

        // Shopify Section Rendering を localize された /search URL 経由で叩く。
        // window.Shopify.routes.search_url は locale prefix 込み (例: /en/search)
        // なので、Markets の通貨換算 + 言語が <product-card> HTML / 内部 <a href> に
        // 反映される。fallback で素の '/search' (= default locale)。
        //
        // 加えて: 価格フィルタ等で fetch するときに cookie だけで Markets country
        // context が伝わらないケースが観測されたため、data-country / data-locale-iso
        // から URL に country / locale を明示注入する (Shopify Markets の query param
        // 仕様)。これで「価格フィルタ後に grid が JPY に戻る」現象を回避。
        const idQuery = productIds.map((id) => 'id:' + id).join(' OR ');
        const searchBase =
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.search_url) || '/search';
        const params = new URLSearchParams();
        params.set('q', idQuery);
        params.set('sections', 'v4c-search-batch');
        const country = this.dataset.country;
        const localeIso = this.dataset.localeIso;
        if (country) params.set('country', country);
        if (localeIso) params.set('locale', localeIso);
        const url = searchBase + '?' + params.toString();
        const res = await fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error('search-batch status ' + res.status);
        const json = await res.json();
        const html = json['v4c-search-batch'] || '';

        const tpl = document.createElement('template');
        tpl.innerHTML = html;
        const cardsByPid = new Map();
        tpl.content.querySelectorAll('product-card').forEach((c) => {
          const pid = c.dataset.productId;
          if (pid) cardsByPid.set(pid, c);
        });

        // V4C ranking 順に並べる
        if (this.grid) {
          this.grid.querySelectorAll('.product-grid__item--skeleton').forEach((el) => el.remove());
          const fragment = document.createDocumentFragment();
          for (let i = 0; i < productIds.length; i++) {
            const card = cardsByPid.get(productIds[i]);
            if (!card) continue;
            const li = document.createElement('li');
            li.className = 'product-grid__item product-grid__item--' + i;
            li.dataset.productId = productIds[i];
            li.appendChild(card);
            fragment.appendChild(li);
          }
          this.grid.appendChild(fragment);
        }

        this.loadedCount += productIds.length;
        this.page += 1;
        if (this.totalSize > 0 && this.loadedCount >= this.totalSize) this.allLoaded = true;
        if (productIds.length < this.pageSize) this.allLoaded = true;

        this.classList.add('v4c-loaded');
      } catch (e) {
        console.error('[v4c-results-list]', e);
        this.classList.add('v4c-error');
      } finally {
        this.loading = false;
      }
    }

    _publishSearchCompleted(v4cResult) {
      try {
        const publish = window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish;
        if (typeof publish !== 'function') return;
        const ids = (v4cResult.results || [])
          .map((r) => r.v4cId)
          .filter(Boolean)
          .slice(0, 24)
          .map((id) => ({ id: id }));
        publish('v4c_search_completed', {
          query: this.query,
          productDetails: ids,
          totalSize: v4cResult.totalSize || 0,
          source: v4cResult.source || '',
          // search レスポンスの attributionToken を pixel に直接受け渡す。
          // pixel 側は localStorage よりこちらを優先し、後続の DPV / ATC / purchase
          // にも in-memory memo として再利用する (V4C attribution の取りこぼし低減)。
          attributionToken: v4cResult.attributionToken || '',
        });
      } catch (e) {
        console.warn('[v4c-results-list] publish v4c_search_completed failed', e);
      }
    }

    updateDocumentTitle() {
      if (this.totalSize === null || !this.query) return;
      const currentTitle = document.title || '';
      const dashMatch = currentTitle.match(/\s+[–\-]\s+(.+)$/);
      const suffix = dashMatch ? ' – ' + dashMatch[1] : '';
      // tmpl 例: 'Search: "__Q__" — __COUNT__ results' (data attribute から)
      const tmpl = this.dataset.localeTitleTmpl || '__Q__ __COUNT__';
      const body = tmpl
        .replace('__Q__', this.query)
        .replace('__COUNT__', String(this.totalSize));
      document.title = body + suffix;
    }

    setupInfiniteScroll() {
      if (!this.sentinel) return;
      const self = this;
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        if (self.loading || self.allLoaded) return;
        self.loadNextPage();
      }, { rootMargin: '300px' });
      io.observe(this.sentinel);
      this._io = io;
    }
  }

  customElements.define('v4c-results-list', V4cResultsListComponent);
})();
